import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  loadMenuItems,
  loadCategories,
  getMenuItemsByBrand,
  loadAddonsForItem,
  loadModifierGroupsFor,
  normalizeBrand,
  sortCategoriesForBrand,
  MenuItem,
  AddOn,
  ModifierGroup,
} from '../services/menuService';
import { useCart, CartAddon } from '../context/CartContext';
import { getHiddenCategories } from '../config/branchContext';
import { useOrder } from '../context/OrderContext';
import { useLanguage } from '../context/LanguageContext';
import { useBrand } from '../context/BrandContext';
import { useAuth } from '../context/AuthContext';
import BrandLogo from '../components/BrandLogo';
import CheckoutPage from './CheckoutPage';

const MenuPage: React.FC = () => {
  const { tableNumber, brand: brandParam } = useParams<{ tableNumber: string; brand: string }>();
  const navigate = useNavigate();
  const { addItem, total, itemCount, items: cartItems, updateQuantity, removeItem } = useCart();
  const { mode, tableNumber: orderTable, setMode } = useOrder();
  const { lang, setLang, t } = useLanguage();
  const { brand } = useBrand();
  const { user } = useAuth();

  const isDineIn = mode === 'dine_in' && (tableNumber || orderTable !== '0');
  const displayTable = tableNumber || orderTable;

  const [grouped, setGrouped] = useState<Map<string, MenuItem[]>>(new Map());
  const [categories, setCategories] = useState<string[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Checkout modal state (desktop flow; mobile still navigates to /cart)
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);

  // Detail modal state
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [detailQuantity, setDetailQuantity] = useState(1);
  const [detailNotes, setDetailNotes] = useState('');
  const [detailAddons, setDetailAddons] = useState<AddOn[]>([]);
  const [selectedAddonIds, setSelectedAddonIds] = useState<Set<string>>(new Set());
  const [addonsLoading, setAddonsLoading] = useState(false);

  // Modifier groups (Size / Crust / Sauce / Toppings) — Domino's style
  const [modifierGroups, setModifierGroups] = useState<ModifierGroup[]>([]);
  const [selections, setSelections] = useState<Record<string, string[]>>({});
  // When set, Add-to-cart replaces the existing entry instead of pushing a new one.
  const [editingCartId, setEditingCartId] = useState<string | null>(null);

  const categoryRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Normalise legacy slugs like `pizza-don-vito` so the filter matches correctly.
  const brandKey = normalizeBrand(brandParam);
  const brandLabel = brandKey === 'donvito' ? 'Pizza Don Vito' : brandKey === 'sushiki' ? 'Sushiki' : '';

  // Navigation helpers — work for both flows
  const backPath = isDineIn ? `/table/${displayTable}` : '/';
  const cartPath = isDineIn ? `/table/${displayTable}/cart` : '/cart';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([loadMenuItems(), loadCategories()])
      .then(([items, cats]) => {
        if (cancelled) return;
        const rawMap = getMenuItemsByBrand(items, brandKey);
        // Per-branch category filter: Köln hides pizza/pasta/insalata categories
        const hiddenCats = getHiddenCategories();
        const isHidden = (cat: string) => hiddenCats.includes(cat.toLowerCase().trim());
        const map = new Map<string, MenuItem[]>();
        rawMap.forEach((v, k) => { if (!isHidden(k)) map.set(k, v); });
        setGrouped(map);
        // Build category order using admin-defined per-brand display_order.
        // Start from the Firestore-sorted category list, then append any
        // menu-only categories (auto-extracted, no Firestore doc) at the end
        // preserving the map's insertion order.
        const sortedCats = sortCategoriesForBrand(cats, brandKey);
        const byNameLower = new Map<string, number>();
        sortedCats.forEach((c, i) => byNameLower.set((c.name || '').toLowerCase(), i));
        // Client-requested grouping (Sadik 2026-05-21): Sushi categories first,
        // then Bowls, then Pizza, then Pasta, then the rest. Mirror of the POS
        // sort so customers and cashiers see categories in the same order.
        const CATEGORY_PRIORITY = [
          'maki', 'nigiri', 'sashimi', 'inside out', 'inside-out special',
          'sushi boxen', 'crispy roll', 'poke bowl', 'pizza', 'pasta',
        ];
        const priorityRank = (name: string): number => {
          const n = (name || '').toLowerCase().trim();
          for (let i = 0; i < CATEGORY_PRIORITY.length; i++) {
            if (n.startsWith(CATEGORY_PRIORITY[i])) return i;
          }
          return 999;
        };
        const mapKeys = Array.from(map.keys());
        const ordered = [...mapKeys].sort((a, b) => {
          const ra = priorityRank(a);
          const rb = priorityRank(b);
          if (ra !== rb) return ra - rb;
          // Within the same priority bucket fall back to admin display_order,
          // then alphabetical.
          const ia = byNameLower.has(a.toLowerCase()) ? byNameLower.get(a.toLowerCase())! : 999999;
          const ib = byNameLower.has(b.toLowerCase()) ? byNameLower.get(b.toLowerCase())! : 999999;
          if (ia !== ib) return ia - ib;
          return a.localeCompare(b);
        });
        setCategories(ordered);
        if (ordered.length > 0) setActiveCategory(ordered[0]);
      })
      .catch((err) => {
        if (!cancelled) setError(t('error'));
        console.error(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [brandKey]);

  const scrollToCategory = (cat: string) => {
    setActiveCategory(cat);
    const el = categoryRefs.current.get(cat);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const openDetail = async (item: MenuItem) => {
    // Sold-out items are visible on the menu but not orderable.
    if (item.sold_out) return;
    setSelectedItem(item);
    setDetailQuantity(1);
    setDetailNotes('');
    setSelectedAddonIds(new Set());
    setDetailAddons([]);
    setModifierGroups([]);
    setSelections({});
    setAddonsLoading(true);
    try {
      const [addons, groups] = await Promise.all([
        loadAddonsForItem(item.id),
        loadModifierGroupsFor(item),
      ]);
      setDetailAddons(addons);
      setModifierGroups(groups);
      // Pre-select defaults (e.g. Tomaten), and first-available for required single groups
      const initial: Record<string, string[]> = {};
      groups.forEach((g) => {
        const defaults = g.options.filter((o) => o.is_default && o.is_available).map((o) => o.id);
        if (defaults.length > 0) {
          initial[g.id] = g.type === 'single' ? [defaults[0]] : defaults;
        } else if (g.required && g.type === 'single' && g.options.length > 0) {
          const firstAvail = g.options.find((o) => o.is_available);
          if (firstAvail) initial[g.id] = [firstAvail.id];
        }
      });
      setSelections(initial);
    } catch {
      setDetailAddons([]);
      setModifierGroups([]);
    } finally {
      setAddonsLoading(false);
    }
  };

  const toggleModifier = (g: ModifierGroup, optionId: string) => {
    setSelections((prev) => {
      const current = prev[g.id] || [];
      let next: string[];
      if (g.type === 'single') {
        if (current[0] === optionId) next = g.required ? current : [];
        else next = [optionId];
      } else {
        if (current.includes(optionId)) next = current.filter((id) => id !== optionId);
        else {
          if (g.max_select && current.length >= g.max_select) return prev;
          next = [...current, optionId];
        }
      }
      // Bundle reconciliation — selecting "All 3 Dips" drops the individuals
      // (and vice versa) so the customer is never double-billed.
      if (g.type === 'multi' && Array.isArray(g.bundle_options)) {
        const opt = g.options.find((o) => o.id === optionId);
        for (const b of g.bundle_options) {
          const justEnabled = !current.includes(optionId) && next.includes(optionId);
          if (!justEnabled) continue;
          if (opt?.is_bundle && b.bundle_option_id === optionId) {
            next = next.filter((id) => !b.contained_option_ids.includes(id));
          } else if (b.contained_option_ids.includes(optionId)) {
            next = next.filter((id) => id !== b.bundle_option_id);
          }
        }
      }
      return { ...prev, [g.id]: next };
    });
  };

  const closeDetail = () => {
    setSelectedItem(null);
    setEditingCartId(null);
  };

  const openEditCartItem = async (cartItem: any) => {
    // Find the original menu item (needed for image, description, pricing fallback)
    let menuItem: MenuItem | undefined;
    grouped.forEach((list) => {
      if (menuItem) return;
      const found = list.find((m) => m.id === cartItem.menuItemId);
      if (found) menuItem = found;
    });
    if (!menuItem) return;
    if (menuItem.sold_out) return;

    setSelectedItem(menuItem);
    setDetailQuantity(cartItem.quantity || 1);
    setDetailNotes(cartItem.notes || '');
    setSelectedAddonIds(new Set((cartItem.addons || []).map((a: any) => a.id)));
    setDetailAddons([]);
    setModifierGroups([]);
    setSelections({});
    setEditingCartId(cartItem.cartId);
    setAddonsLoading(true);
    try {
      const [addons, groups] = await Promise.all([
        loadAddonsForItem(menuItem.id),
        loadModifierGroupsFor(menuItem),
      ]);
      setDetailAddons(addons);
      setModifierGroups(groups);
      // Use saved selections if present; otherwise fall back to defaults
      if (cartItem.selections && typeof cartItem.selections === 'object') {
        setSelections(cartItem.selections);
      } else {
        const initial: Record<string, string[]> = {};
        groups.forEach((g) => {
          const defaults = g.options.filter((o) => o.is_default && o.is_available).map((o) => o.id);
          if (defaults.length > 0) initial[g.id] = g.type === 'single' ? [defaults[0]] : defaults;
        });
        setSelections(initial);
      }
    } catch {
      setDetailAddons([]);
      setModifierGroups([]);
    } finally {
      setAddonsLoading(false);
    }
  };

  const toggleAddon = (id: string) => {
    setSelectedAddonIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Pricing derived from current modifier selections.
  // Sadik 2026-05-30: ingredients use a size/type matrix (e.g. Mushrooms is
  // €2 on small pizza, €3 on large pizza). The size option carries
  // `sets_product_type` which upgrades the effective product_type at runtime,
  // and the resolver picks the right cell from `prices_by_size_type`.
  const sizeGroup = modifierGroups.find((g) => g.applies_to_size);
  const sizeOptionId = sizeGroup ? selections[sizeGroup.id]?.[0] || null : null;
  const sizeOption = sizeGroup && sizeOptionId ? sizeGroup.options.find((o) => o.id === sizeOptionId) : null;
  const effectiveProductType: string = sizeOption?.sets_product_type
    || (selectedItem as any)?.product_type
    || 'default';

  const resolveOptionPrice = (opt: any): number => {
    if (opt?.prices_by_size_type) {
      if (opt.prices_by_size_type[effectiveProductType] !== undefined) return opt.prices_by_size_type[effectiveProductType];
      if (opt.prices_by_size_type.default !== undefined) return opt.prices_by_size_type.default;
    }
    if (sizeOptionId && opt?.price_delta_by_size?.[sizeOptionId] !== undefined) {
      return opt.price_delta_by_size[sizeOptionId];
    }
    return opt?.price_delta || 0;
  };

  const resolvedBase = (() => {
    let base = selectedItem?.price_with_tax || 0;
    if (sizeOption && sizeOption.price_delta > 0) base = sizeOption.price_delta;
    let deltas = 0;
    modifierGroups.forEach((g) => {
      if (g.applies_to_size) return;
      (selections[g.id] || []).forEach((optId) => {
        const opt = g.options.find((o) => o.id === optId);
        if (opt) deltas += resolveOptionPrice(opt);
      });
    });
    return base + deltas;
  })();

  const missingRequired = modifierGroups.filter((g) => {
    if (!g.required) return false;
    const picked = selections[g.id] || [];
    if (g.type === 'single') return picked.length === 0;
    const min = g.min_select ?? 1;
    return picked.length < min;
  });

  const handleAddToCart = () => {
    if (!selectedItem) return;
    if (missingRequired.length > 0) return;
    const chosenAddons: CartAddon[] = detailAddons
      .filter((a) => selectedAddonIds.has(a.id))
      .map((a) => ({ id: a.id, name: a.name, name_de: a.name_de, price: a.price }));

    const snapshot = modifierGroups.length > 0
      ? modifierGroups.map((g) => ({
          id: g.id,
          name: g.name,
          name_de: g.name_de,
          options: g.options.map((o) => ({ id: o.id, name: o.name, name_de: o.name_de, is_default: o.is_default })),
        }))
      : undefined;

    if (editingCartId) {
      // Editing an existing line → drop the original, then add the replacement.
      removeItem(editingCartId);
    }
    addItem({
      menuItemId: selectedItem.id,
      name: selectedItem.name,
      name_de: selectedItem.name_de,
      price: resolvedBase || selectedItem.price_with_tax,
      quantity: detailQuantity,
      categoryName: selectedItem.category,
      imageUrl: selectedItem.image_url,
      notes: detailNotes,
      addons: chosenAddons,
      selections: modifierGroups.length > 0 ? selections : undefined,
      modifier_snapshot: snapshot,
    });
    closeDetail();
  };

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(price);

  // Desktop checkout button: open modal. Dine-in has its own submit flow through CartPage.
  const handleCheckoutClick = () => {
    if (isDineIn) {
      navigate(cartPath);
      return;
    }
    if (!user) {
      navigate('/auth', { state: { from: window.location.pathname + window.location.search } });
      return;
    }
    setCheckoutModalOpen(true);
  };

  if (loading) {
    return (
      <div className="page menu-page">
        <header className="menu-header">
          <button className="back-btn" onClick={() => navigate(backPath)}>&#8592;</button>
          <div className="header-info">
            <span className="header-brand">{brandLabel}</span>
            {isDineIn && <span className="header-table">{t('table')} {displayTable}</span>}
            {!isDineIn && <span className="header-table">{mode === 'delivery' ? t('delivery') : t('pickup')}</span>}
          </div>
        </header>
        <div className="category-chips">
          {[1,2,3,4,5].map(i => (
            <div key={i} className="chip" style={{ width: 70, height: 32, background: '#e0e0e0', borderColor: '#e0e0e0' }}>&nbsp;</div>
          ))}
        </div>
        <div className="menu-items-container">
          <div className="items-grid">
            {[1,2,3,4].map(i => (
              <div key={i} className="menu-card" style={{ minHeight: 180 }}>
                <div className="card-img-wrap" style={{ background: '#e8e8e8', animation: 'pulse 1.5s infinite' }} />
                <div className="card-body">
                  <div style={{ width: '80%', height: 14, background: '#e0e0e0', borderRadius: 4, marginBottom: 6 }} />
                  <div style={{ width: '40%', height: 14, background: '#e8f5e9', borderRadius: 4 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page loading-page">
        <p className="error-text">{error}</p>
        <button className="btn-primary" onClick={() => window.location.reload()}>
          {t('tryAgain')}
        </button>
      </div>
    );
  }

  // FREECOLA campaign banner: shows when the user landed via a flyer QR
  // (`?promo=FREECOLA` set by PromoBridge in App.tsx).
  const activePromo = (() => {
    try { return sessionStorage.getItem('qr_active_promo'); } catch { return null; }
  })();

  return (
    <div className="page menu-page">
      {activePromo === 'FREECOLA' && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0,
            zIndex: 1000,
            padding: '8px 14px',
            background: 'linear-gradient(135deg, #94e815 0%, #65a30d 100%)',
            color: '#0c1b14',
            fontSize: 13,
            fontWeight: 700,
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            letterSpacing: '0.01em',
          }}
        >
          🥤 Gratis Coca-Cola wird beim Checkout hinzugefügt · Free Coca-Cola added at checkout
        </div>
      )}
      {/* Desktop left sidebar (hidden on mobile via CSS) */}
      <aside className="menu-sidebar">
        <div className="brand-mark" style={{ justifyContent: 'center', padding: '8px 0' }}>
          <BrandLogo size={brand.id === 'donvito' ? 120 : 120} showTagline={false} />
        </div>

        {!isDineIn && (
          <>
            <div className="sidebar-section-label">{t('orderType') || 'Order Type'}</div>
            <div className="mode-toggle">
              <button className={mode === 'delivery' ? 'active' : ''} onClick={() => setMode('delivery')}>
                🚴 {t('delivery')}
              </button>
              <button className={mode === 'pickup' ? 'active' : ''} onClick={() => setMode('pickup')}>
                🏠 {t('pickup')}
              </button>
            </div>
          </>
        )}
        {isDineIn && (
          <div className="sidebar-section-label" style={{ color: 'var(--brand-primary)' }}>
            🍽 {t('table')} {displayTable}
          </div>
        )}

        <div className="sidebar-section-label">{t('menu') || 'Menu'}</div>

        {/* Desktop sidebar search */}
        <div className="sidebar-search">
          <span className="sidebar-search-icon">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </span>
          <input
            type="text"
            className="sidebar-search-input"
            placeholder={t('searchMenu')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="sidebar-search-clear" onClick={() => setSearchQuery('')} aria-label="Clear search">
              &times;
            </button>
          )}
        </div>

        {searchQuery ? (
          /* When searching: show result count instead of category list */
          <div className="sidebar-search-results-info">
            {(() => {
              const q = searchQuery.toLowerCase().trim();
              const words = q.split(/\s+/).filter(w => w.length > 0);
              const count = categories.flatMap(c => grouped.get(c) || []).filter(item => {
                const nameEn = (item.name || '').toLowerCase();
                const nameDe = (item.name_de || '').toLowerCase();
                const cat = (item.category || '').toLowerCase();
                return words.every(w => nameEn.includes(w) || nameDe.includes(w) || cat.includes(w));
              }).length;
              return <span>{count} {t('results') || 'results'}</span>;
            })()}
          </div>
        ) : (
        <div className="sidebar-categories">
          <button
            className={`sidebar-cat-btn ${activeCategory === '__all__' ? 'active' : ''}`}
            onClick={() => { setActiveCategory('__all__'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          >
            <span>{t('all')}</span>
            <span className="count">{categories.reduce((n, c) => n + (grouped.get(c)?.length || 0), 0)}</span>
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              className={`sidebar-cat-btn ${activeCategory === cat ? 'active' : ''}`}
              onClick={() => scrollToCategory(cat)}
            >
              <span>{cat}</span>
              <span className="count">{grouped.get(cat)?.length || 0}</span>
            </button>
          ))}
        </div>
        )}

        <div className="sidebar-bottom">
          <button className="back-btn" onClick={() => navigate(backPath)}>&#8592; {t('back') || 'Back'}</button>
          <div className="lang-toggle-landing">
            <button className={lang === 'de' ? 'active' : ''} onClick={() => setLang('de')}>DE</button>
            <button className={lang === 'en' ? 'active' : ''} onClick={() => setLang('en')}>EN</button>
          </div>
        </div>
      </aside>

      <header className="menu-header">
        <button className="back-btn" onClick={() => navigate(backPath)}>&#8592;</button>
        <div className="header-info">
          <span className="header-brand">{brandLabel}</span>
          {isDineIn && <span className="header-table">{t('table')} {displayTable}</span>}
          {!isDineIn && <span className="header-table">{mode === 'delivery' ? t('delivery') : t('pickup')}</span>}
        </div>
        <div className="lang-toggle" style={{ position: 'relative', top: 'auto', right: 'auto', marginLeft: 'auto' }}>
          <button className={lang === 'de' ? 'active' : ''} onClick={() => setLang('de')}>DE</button>
          <button className={lang === 'en' ? 'active' : ''} onClick={() => setLang('en')}>EN</button>
        </div>
      </header>

      {/* Search bar */}
      <div className="search-bar-wrap">
        <div className="search-bar">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="search-input"
            placeholder={t('searchMenu')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="search-clear" onClick={() => setSearchQuery('')}>&times;</button>
          )}
        </div>
      </div>

      {/* Category chips — hide when searching */}
      {!searchQuery && (
        <div className="category-chips">
          <button
            className={`chip ${activeCategory === '__all__' ? 'active' : ''}`}
            onClick={() => { setActiveCategory('__all__'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          >
            {t('all')}
          </button>
          {categories.map((cat) => (
            <button key={cat} className={`chip ${activeCategory === cat ? 'active' : ''}`}
              onClick={() => scrollToCategory(cat)}>
              {cat}
            </button>
          ))}
        </div>
      )}

      <div className="menu-items-container">
        {(() => {
          const q = searchQuery.toLowerCase().trim();
          const isSearching = q.length > 0;

          // When searching: show flat filtered results
          if (isSearching) {
            const allItems = categories.flatMap((cat) => grouped.get(cat) || []);
            // Split query into words for multi-word matching (e.g. "cheese onion" matches "Cheese Onion Roll")
            const words = q.split(/\s+/).filter(w => w.length > 0);
            const filtered = allItems.filter((item) => {
              const nameEn = (item.name || '').toLowerCase();
              const nameDe = (item.name_de || '').toLowerCase();
              const cat = (item.category || '').toLowerCase();
              // Every word must match in name or category
              return words.every(w => nameEn.includes(w) || nameDe.includes(w) || cat.includes(w));
            });

            if (filtered.length === 0) {
              return (
                <div className="search-no-results">
                  <p>{t('noResults')} "{searchQuery}"</p>
                </div>
              );
            }

            return (
              <div className="category-section">
                <h2 className="category-title">{filtered.length} {t('results')}</h2>
                <div className="items-grid">
                  {filtered.map((item) => (
                    <button
                      key={item.id}
                      className={`menu-card${item.sold_out ? ' menu-card--sold-out' : ''}`}
                      onClick={() => openDetail(item)}
                      disabled={item.sold_out}
                      aria-disabled={item.sold_out}
                    >
                      {item.image_url && (
                        <div className="card-img-wrap">
                          <img src={item.image_url} alt={item.name_de || item.name} loading="lazy" />
                          {item.sold_out && <div className="sold-out-ribbon">{t('soldOut') || 'AUSVERKAUFT'}</div>}
                        </div>
                      )}
                      <div className="card-body">
                        <span className="card-name">{item.name_de || item.name}</span>
                        {item.description_de && <span className="card-desc">{item.description_de}</span>}
                        <span className="card-price">{formatPrice(item.price_with_tax)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          }

          // Normal: grouped by category
          return categories.map((cat) => {
            const catItems = grouped.get(cat) || [];
            return (
              <div key={cat} ref={(el) => { if (el) categoryRefs.current.set(cat, el); }} className="category-section">
                <h2 className="category-title">{cat}</h2>
                <div className="items-grid">
                  {catItems.map((item) => (
                  <button
                    key={item.id}
                    className={`menu-card${item.sold_out ? ' menu-card--sold-out' : ''}`}
                    onClick={() => openDetail(item)}
                    disabled={item.sold_out}
                    aria-disabled={item.sold_out}
                  >
                    {item.image_url && (
                      <div className="card-img-wrap">
                        <img src={item.image_url} alt={item.name_de || item.name} loading="lazy" />
                        {item.sold_out && <div className="sold-out-ribbon">{t('soldOut') || 'AUSVERKAUFT'}</div>}
                      </div>
                    )}
                    <div className="card-body">
                      <span className="card-name">{item.name_de || item.name}</span>
                      {item.description_de && <span className="card-desc">{item.description_de}</span>}
                      <span className="card-price">{formatPrice(item.price_with_tax)}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        });
        })()}
      </div>

      {itemCount > 0 && (
        <button className="floating-cart-btn" onClick={() => navigate(cartPath)}>
          🛒 {t('cart')} ({itemCount}) &mdash; {formatPrice(total)}
        </button>
      )}

      {/* Desktop right cart panel (hidden on mobile via CSS) */}
      <aside className="menu-cart-panel">
        <div className="cart-panel-header">
          <h3>🛒 {t('cart')}</h3>
          <span className="count-pill">{itemCount} {itemCount === 1 ? (t('item') || 'item') : (t('items') || 'items')}</span>
        </div>
        {cartItems.length === 0 ? (
          <div className="cart-panel-empty">
            <div className="big-icon">🍽</div>
            <div style={{ fontWeight: 600, color: 'var(--brand-text)', marginBottom: 6 }}>
              {t('emptyCart') || (lang === 'de' ? 'Warenkorb ist leer' : 'Your cart is empty')}
            </div>
            <div style={{ fontSize: '0.82rem' }}>
              {lang === 'de' ? 'Wähle ein Gericht aus der Karte aus.' : 'Pick something delicious from the menu.'}
            </div>
          </div>
        ) : (
          <div className="cart-panel-items">
            {cartItems.map((item) => {
              const addonTotal = item.addons.reduce((s, a) => s + a.price, 0);
              const lineTotal = (item.price + addonTotal) * item.quantity;
              const modifierSummary: string[] = [];
              if (item.modifier_snapshot && item.selections) {
                item.modifier_snapshot.forEach((g) => {
                  const picked = item.selections?.[g.id] || [];
                  picked.forEach((optId) => {
                    const opt = g.options.find((o) => o.id === optId);
                    if (opt) modifierSummary.push(lang === 'de' ? (opt.name_de || opt.name) : opt.name);
                  });
                });
              }
              if (item.addons.length > 0) {
                item.addons.forEach((a) => {
                  const n = lang === 'de' ? (a.name_de || a.name) : a.name;
                  if (n) modifierSummary.push(n);
                });
              }
              return (
                <div key={item.cartId} className="cart-panel-row">
                  <div className="qty-badge">{item.quantity}×</div>
                  <div>
                    <div className="name">{item.name_de || item.name}</div>
                    {modifierSummary.length > 0 && (
                      <div style={{ fontSize: '0.72rem', color: 'var(--brand-text-muted)', marginTop: 2 }}>
                        {modifierSummary.join(' · ')}
                      </div>
                    )}
                    {item.notes && (
                      <div style={{ fontSize: '0.72rem', color: '#f59e0b', fontStyle: 'italic', marginTop: 2 }}>
                        {item.notes}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                      <button
                        onClick={() => openEditCartItem(item)}
                        style={{ padding: '2px 8px', borderRadius: 6, background: 'rgba(25,118,210,0.15)', color: '#60a5fa', fontSize: '0.8rem' }}
                        title={lang === 'de' ? 'Bearbeiten' : 'Edit'}
                      >✎</button>
                      <button
                        onClick={() => item.quantity <= 1 ? removeItem(item.cartId) : updateQuantity(item.cartId, item.quantity - 1)}
                        style={{ padding: '2px 8px', borderRadius: 6, background: 'rgba(255,255,255,0.06)', color: 'var(--brand-text-muted)', fontSize: '0.8rem' }}
                      >{item.quantity <= 1 ? '🗑' : '−'}</button>
                      <button
                        onClick={() => updateQuantity(item.cartId, item.quantity + 1)}
                        style={{ padding: '2px 8px', borderRadius: 6, background: 'rgba(255,255,255,0.06)', color: 'var(--brand-text-muted)', fontSize: '0.8rem' }}
                      >+</button>
                    </div>
                  </div>
                  <div className="price">{formatPrice(lineTotal)}</div>
                </div>
              );
            })}
          </div>
        )}
        <div className="cart-panel-footer">
          <div className="cart-panel-total">
            <span>{t('total')}</span>
            <span className="amount">{formatPrice(total)}</span>
          </div>
          <button
            className="cart-panel-cta"
            disabled={itemCount === 0}
            onClick={handleCheckoutClick}
          >
            {isDineIn
              ? (lang === 'de' ? 'Jetzt bestellen' : 'Place Order')
              : (lang === 'de' ? 'Zur Kasse' : 'Checkout')} →
          </button>
        </div>
      </aside>

      {checkoutModalOpen && (
        <div className="checkout-modal-overlay" onClick={() => setCheckoutModalOpen(false)}>
          <div className="checkout-modal-card" onClick={(e) => e.stopPropagation()}>
            <CheckoutPage inModal onClose={() => setCheckoutModalOpen(false)} />
          </div>
        </div>
      )}

      {selectedItem && (
        <div className="modal-overlay" onClick={closeDetail}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeDetail}>&times;</button>
            {selectedItem.image_url && (
              <img className="modal-img" src={selectedItem.image_url} alt={selectedItem.name_de || selectedItem.name} />
            )}
            <h2 className="modal-title">{selectedItem.name_de || selectedItem.name}</h2>
            {selectedItem.description_de && <p className="modal-desc">{selectedItem.description_de}</p>}
            <p className="modal-price">{formatPrice(resolvedBase || selectedItem.price_with_tax)}</p>

            {addonsLoading && <p className="addon-loading">{t('extrasLoading')}</p>}

            {modifierGroups.map((g) => {
              const picked = selections[g.id] || [];
              const isMissing = missingRequired.includes(g);
              const groupLabel = lang === 'de' ? (g.name_de || g.name) : g.name;
              return (
                <div key={g.id} className="addons-section" style={{ marginBottom: 16 }}>
                  <h3 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: isMissing ? '#ff6b6b' : undefined }}>
                    <span>{groupLabel}</span>
                    <span style={{ fontSize: '0.7rem', fontWeight: 600, color: g.required ? '#ff9800' : 'var(--brand-text-muted)', textTransform: 'uppercase' }}>
                      {g.required ? (lang === 'de' ? 'Pflichtfeld' : 'Required') : (lang === 'de' ? 'Optional' : 'Optional')}
                    </span>
                  </h3>
                  {g.options.map((opt) => {
                    const selected = picked.includes(opt.id);
                    const displayDelta = resolveOptionPrice(opt);
                    const optLabel = lang === 'de' ? (opt.name_de || opt.name) : opt.name;
                    return (
                      <label key={opt.id} className="addon-row" onClick={(e) => { e.preventDefault(); toggleModifier(g, opt.id); }}>
                        <input
                          type={g.type === 'single' ? 'radio' : 'checkbox'}
                          name={`mg_${g.id}`}
                          checked={selected}
                          onChange={() => toggleModifier(g, opt.id)}
                        />
                        <span className="addon-name">{optLabel}</span>
                        {displayDelta !== 0 && (
                          <span className="addon-price">
                            {g.applies_to_size
                              ? formatPrice(displayDelta)
                              : (displayDelta > 0 ? `+${formatPrice(displayDelta)}` : `−${formatPrice(Math.abs(displayDelta))}`)}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              );
            })}

            {(() => {
              const catStr = (selectedItem?.category || '').toString().toLowerCase();
              const isPizza = catStr === 'pizza' || catStr.startsWith('pizza');
              const suppressLegacy = isPizza || modifierGroups.length > 0;
              return !suppressLegacy && detailAddons.length > 0 ? (
                <div className="addons-section">
                  <h3>{t('extras')}</h3>
                  {detailAddons.map((addon) => (
                    <label key={addon.id} className="addon-row">
                      <input type="checkbox" checked={selectedAddonIds.has(addon.id)} onChange={() => toggleAddon(addon.id)} />
                      <span className="addon-name">{addon.name_de || addon.name}</span>
                      <span className="addon-price">+{formatPrice(addon.price)}</span>
                    </label>
                  ))}
                </div>
              ) : null;
            })()}

            <div className="notes-section">
              <label htmlFor="item-notes">{t('itemNotes')}</label>
              <textarea id="item-notes" placeholder={t('itemNotesPlaceholder')}
                value={detailNotes} onChange={(e) => setDetailNotes(e.target.value)} rows={2} />
            </div>

            <div className="quantity-row">
              <button className="qty-btn" onClick={() => setDetailQuantity((q) => Math.max(1, q - 1))}>&minus;</button>
              <span className="qty-value">{detailQuantity}</span>
              <button className="qty-btn" onClick={() => setDetailQuantity((q) => q + 1)}>+</button>
            </div>

            <button
              className="btn-primary add-to-cart-btn"
              onClick={handleAddToCart}
              disabled={missingRequired.length > 0}
              style={missingRequired.length > 0 ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            >
              {missingRequired.length > 0
                ? (lang === 'de' ? 'Bitte Pflichtfelder auswählen' : 'Please select required options')
                : (
                  <>
                    {editingCartId
                      ? (lang === 'de' ? 'Aktualisieren' : 'Update')
                      : t('addToCart')}{' '}
                    &mdash;{' '}
                    {formatPrice(
                      ((resolvedBase || selectedItem.price_with_tax) +
                        detailAddons.filter((a) => selectedAddonIds.has(a.id)).reduce((s, a) => s + a.price, 0)) *
                        detailQuantity
                    )}
                  </>
                )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuPage;
